import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CrudStyles } from '../shared/Crud';
import MessageModal from '../shared/MessageModal';
import TransportCompanyLogo from '../shared/TransportCompanyLogo';
import transportCompanyLogoService from '../../services/transportCompanyLogoService';
import { TRANSPORT_COMPANY_LOGO_FORMATS, TRANSPORT_COMPANY_LOGO_RULES as RULES } from '../../constants/transportCompanyLogo';
import {
    extractFieldErrors,
    getFriendlyErrorMessage,
    parseApiError,
} from '../../utils/errorHandler';
import { inspectPngFile, inspectSvgFile, validatePair } from '../../utils/transportCompanyLogoValidation';
import {
    Actions,
    FieldError,
    FileInput,
    Hint,
    PreviewBlock,
    PreviewFrame,
    PreviewLabel,
    Previews,
    SettingsForm,
} from './styles';

const { FormRow, FormGroup, Button } = CrudStyles;

function mapLogoFieldLocation(location) {
    const field = String(location || '').split('.').pop()?.toLowerCase();
    if (field === 'png' || field === 'svg') return field;
    return field || null;
}

export default function LogoSettingsForm() {
    const [pngFile, setPngFile] = useState(null);
    const [svgFile, setSvgFile] = useState(null);
    const [pngPreview, setPngPreview] = useState(null);
    const [svgPreview, setSvgPreview] = useState(null);
    const [pngDims, setPngDims] = useState(null);
    const [svgDims, setSvgDims] = useState(null);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [messageModal, setMessageModal] = useState({ open: false, title: 'Aviso', message: '' });
    const pngInputRef = useRef(null);
    const svgInputRef = useRef(null);
    const pngPreviewRef = useRef(null);
    const svgPreviewRef = useRef(null);
    pngPreviewRef.current = pngPreview;
    svgPreviewRef.current = svgPreview;

    useEffect(() => () => {
        if (pngPreviewRef.current) URL.revokeObjectURL(pngPreviewRef.current);
        if (svgPreviewRef.current) URL.revokeObjectURL(svgPreviewRef.current);
    }, []);

    const pairError = useMemo(
        () => (pngDims && svgDims ? validatePair(pngDims, svgDims) : null),
        [pngDims, svgDims],
    );

    const canSubmit = Boolean(
        pngFile
        && svgFile
        && pngDims
        && svgDims
        && !errors.png
        && !errors.svg
        && !pairError
        && !isSubmitting,
    );

    const replacePreview = (kind, file) => {
        const nextUrl = file ? URL.createObjectURL(file) : null;
        if (kind === 'png') {
            setPngPreview((current) => {
                if (current) URL.revokeObjectURL(current);
                return nextUrl;
            });
            return;
        }
        setSvgPreview((current) => {
            if (current) URL.revokeObjectURL(current);
            return nextUrl;
        });
    };

    const handlePngChange = async (event) => {
        const file = event.target.files?.[0] ?? null;
        setPngFile(file);
        replacePreview('png', file);
        if (!file) {
            setPngDims(null);
            setErrors((prev) => ({ ...prev, png: null }));
            return;
        }
        const result = await inspectPngFile(file);
        setPngDims(result.dimensions);
        setErrors((prev) => ({ ...prev, png: result.error }));
    };

    const handleSvgChange = async (event) => {
        const file = event.target.files?.[0] ?? null;
        setSvgFile(file);
        replacePreview('svg', file);
        if (!file) {
            setSvgDims(null);
            setErrors((prev) => ({ ...prev, svg: null }));
            return;
        }
        const result = await inspectSvgFile(file);
        setSvgDims(result.dimensions);
        setErrors((prev) => ({ ...prev, svg: result.error }));
    };

    const resetFiles = () => {
        setPngFile(null);
        setSvgFile(null);
        setPngDims(null);
        setSvgDims(null);
        replacePreview('png', null);
        replacePreview('svg', null);
        setErrors({});
        if (pngInputRef.current) pngInputRef.current.value = '';
        if (svgInputRef.current) svgInputRef.current.value = '';
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!canSubmit) return;

        setIsSubmitting(true);
        try {
            await transportCompanyLogoService.update({ png: pngFile, svg: svgFile });
            resetFiles();
            setMessageModal({
                open: true,
                title: 'Sucesso',
                message: 'Logo atualizada com sucesso.',
            });
        } catch (error) {
            const apiError = await parseApiError(error);
            const { fieldErrors, additionalMessages } = extractFieldErrors(apiError.details, {
                mapLocation: mapLogoFieldLocation,
            });
            setErrors((prev) => ({
                ...prev,
                png: fieldErrors.png || prev.png,
                svg: fieldErrors.svg || prev.svg,
            }));
            const extras = additionalMessages.filter(Boolean);
            setMessageModal({
                open: true,
                title: 'Erro',
                message: [getFriendlyErrorMessage(apiError), ...extras].filter(Boolean).join('\n\n'),
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {messageModal.open && (
                <MessageModal
                    title={messageModal.title}
                    message={messageModal.message}
                    onClose={() => setMessageModal((current) => ({ ...current, open: false }))}
                />
            )}
            <SettingsForm onSubmit={handleSubmit}>
                <Hint>
                    Banner horizontal usado no cabeçalho, protocolos e relatórios.
                    Referência oficial: {RULES.referenceWidth} × {RULES.referenceHeight} px
                    (proporção ≈ 2,84). Envie PNG e SVG com as mesmas dimensões.
                </Hint>

                <Previews>
                    <PreviewBlock>
                        <PreviewLabel>PNG</PreviewLabel>
                        <PreviewFrame $hasError={Boolean(errors.png)}>
                            {pngPreview ? (
                                <img src={pngPreview} alt="Pré-visualização PNG" />
                            ) : (
                                <TransportCompanyLogo
                                    format={TRANSPORT_COMPANY_LOGO_FORMATS.PNG}
                                    fallback="Nenhuma logo cadastrada"
                                />
                            )}
                        </PreviewFrame>
                    </PreviewBlock>
                    <PreviewBlock>
                        <PreviewLabel>SVG</PreviewLabel>
                        <PreviewFrame $hasError={Boolean(errors.svg)}>
                            {svgPreview ? (
                                <img src={svgPreview} alt="Pré-visualização SVG" />
                            ) : (
                                <TransportCompanyLogo
                                    format={TRANSPORT_COMPANY_LOGO_FORMATS.SVG}
                                    fallback="Nenhuma logo cadastrada"
                                />
                            )}
                        </PreviewFrame>
                    </PreviewBlock>
                </Previews>

                <FormRow>
                    <FormGroup>
                        <label htmlFor="logo-png">Arquivo PNG *</label>
                        <FileInput
                            id="logo-png"
                            ref={pngInputRef}
                            name="png"
                            type="file"
                            accept="image/png,.png"
                            onChange={handlePngChange}
                            $hasError={Boolean(errors.png)}
                        />
                        <Hint>
                            Largura {RULES.minWidth}–{RULES.maxWidth} px, altura {RULES.minHeight}–{RULES.maxHeight} px,
                            até 1 MiB.
                        </Hint>
                        {errors.png && <FieldError>{errors.png}</FieldError>}
                    </FormGroup>
                    <FormGroup>
                        <label htmlFor="logo-svg">Arquivo SVG *</label>
                        <FileInput
                            id="logo-svg"
                            ref={svgInputRef}
                            name="svg"
                            type="file"
                            accept="image/svg+xml,.svg"
                            onChange={handleSvgChange}
                            $hasError={Boolean(errors.svg)}
                        />
                        <Hint>
                            Mesmas dimensões do PNG (tolerância ±{RULES.maxDelta} px). O SVG precisa de width/height ou viewBox.
                        </Hint>
                        {errors.svg && <FieldError>{errors.svg}</FieldError>}
                    </FormGroup>
                </FormRow>

                {pairError && <FieldError>{pairError}</FieldError>}

                <Actions>
                    <Button type="submit" variant="primary" disabled={!canSubmit}>
                        {isSubmitting ? 'Salvando...' : 'Salvar'}
                    </Button>
                </Actions>
            </SettingsForm>
        </>
    );
}
